import { PureComponent, ReactNode } from 'react';
import ReactHotkeys from 'react-hot-keys';
import { GeniePadElementsSettingItem } from '../../../../../api/pushGenie';
import RightPopUp from '../../../../common/RightPopUp';
import ElementsConfigureTable from './ElementsConfigureTable';
import MedicationType from './MedicationType';
import MedicationsOtherSettings from './MedicationsOtherSettings';
import RoutesConfig from './RoutesConfig';

enum SELECTED_TAB {
    CONFIG = 'config',
    TYPES = 'types',
    OTHER_SETTINGS = 'other-settings',
}

interface Props<T> {
    closeMe: () => void;
    onUpdate: (
        config: T[],
        medTypeIds: string[],
        isDoseCalculatorAvailable: boolean,
        medRouteEnabled: boolean,
    ) => void;
    config: T[];
    medTypeIds: string[];
    isDoseCalculatorAvailable: boolean;
    medRouteEnabled: boolean;
}

type State<T> = {
    elements: T[];
    selectedTab: SELECTED_TAB;
    medTypeIds?: string[];
    isDoseCalculatorAvailable: boolean;
    medRouteEnabled: boolean;
};
class MedicationConfigPanel<T extends GeniePadElementsSettingItem> extends PureComponent<
    Props<T>,
    State<T>
> {
    state: State<T> = {
        elements: this.props.config,
        medTypeIds: this.props.medTypeIds,
        isDoseCalculatorAvailable: this.props.isDoseCalculatorAvailable,
        selectedTab: SELECTED_TAB.CONFIG,
        medRouteEnabled: this.props.medRouteEnabled,
    };
    onSave = (): void => {
        const { closeMe, onUpdate } = this.props;
        const { elements, medTypeIds = [], isDoseCalculatorAvailable, medRouteEnabled } = this.state;
        onUpdate(elements, medTypeIds, isDoseCalculatorAvailable, medRouteEnabled);
        closeMe();
    };

    updateElemets = (elements: T[]): void => {
        this.setState({ elements });
    };

    updateMedType = (medTypeIds?: string[]): void => {
        this.setState({ medTypeIds: medTypeIds });
    };

    toggleIsDoseCalculatorAvailable = (): void => {
        const { isDoseCalculatorAvailable } = this.state;
        this.setState({ isDoseCalculatorAvailable: !isDoseCalculatorAvailable });
    };

    componentDidMount() {
        this.setState({
            elements: this.props.config,
            medTypeIds: this.props.medTypeIds,
            isDoseCalculatorAvailable: this.props.isDoseCalculatorAvailable,
            medRouteEnabled: this.props.medRouteEnabled,
        });
    }

    setSelectedMedicationTypes = (tab: SELECTED_TAB): void => {
        this.setState({ selectedTab: tab });
    };

    render(): ReactNode {
        const { closeMe } = this.props;
        const { elements, selectedTab, medTypeIds, isDoseCalculatorAvailable } = this.state;

        return (
            <ReactHotkeys keyName="esc" onKeyDown={closeMe}>
                <RightPopUp closeMe={closeMe} width={'50%'} minWidth={'480px'}>
                    <div className="bg-darwin-neutral-50 flex flex-col h-full space-between">
                        <div className="flex items-center space-x-16 p-16 cursor-pointer">
                            <div
                                onClick={closeMe}
                                className="flex items-center justify-center w-16 h-16"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 448 512"
                                    className="w-16 h-16 fill-darwin-neutral-1000"
                                >
                                    <path d="M447.1 256c0 13.25-10.76 24.01-24.01 24.01H83.9l132.7 126.6c9.625 9.156 9.969 24.41 .8125 33.94c-9.156 9.594-24.34 9.938-33.94 .8125l-176-168C2.695 268.9 .0078 262.6 .0078 256S2.695 243.2 7.445 238.6l176-168C193 61.51 208.2 61.85 217.4 71.45c9.156 9.5 8.812 24.75-.8125 33.94l-132.7 126.6h340.1C437.2 232 447.1 242.8 447.1 256z"></path>
                                </svg>
                            </div>
                            <p className="text-darwin-neutral-1000 heading-2 bold">
                                Configure Medications Table
                            </p>
                        </div>
                        <div className="w-full pl-8 border-b border-neutral-8 select-none">
                            <div className="flex items-center font-600 text-14 leading-28 cursor-pointer ml-8">
                                <div
                                    className={
                                        selectedTab === SELECTED_TAB.CONFIG
                                            ? 'border-b-2 border-actionPrimary  text-14 font-700'
                                            : 'border-b-2 border-transparent text-14 font-700'
                                    }
                                    onClick={() => {
                                        this.setSelectedMedicationTypes(SELECTED_TAB.CONFIG);
                                    }}
                                >
                                    <p
                                        className={
                                            selectedTab === SELECTED_TAB.CONFIG
                                                ? 'pb-8 text-actionPrimary text-14 font-700'
                                                : 'pb-8 text-neutral-3 text-14 font-700'
                                        }
                                    >
                                        Medication Config
                                    </p>
                                </div>
                                <div
                                    className={
                                        selectedTab === SELECTED_TAB.TYPES
                                            ? 'border-b-2 mx-16 border-actionPrimary  text-14 font-700'
                                            : 'border-b-2 mx-16 border-transparent text-14 font-700'
                                    }
                                    onClick={() => {
                                        this.setSelectedMedicationTypes(SELECTED_TAB.TYPES);
                                    }}
                                >
                                    <p
                                        className={
                                            selectedTab === SELECTED_TAB.TYPES
                                                ? 'pb-8 text-actionPrimary text-14 font-700'
                                                : 'pb-8 text-neutral-3 text-14 font-700'
                                        }
                                    >
                                        Medication types
                                    </p>
                                </div>
                                <div
                                    className={
                                        selectedTab === SELECTED_TAB.OTHER_SETTINGS
                                            ? 'border-b-2 mx-16 border-actionPrimary  text-14 font-700'
                                            : 'border-b-2 mx-16 border-transparent text-14 font-700'
                                    }
                                    onClick={() => {
                                        this.setSelectedMedicationTypes(
                                            SELECTED_TAB.OTHER_SETTINGS,
                                        );
                                    }}
                                >
                                    <p
                                        className={
                                            selectedTab === SELECTED_TAB.OTHER_SETTINGS
                                                ? 'pb-8 text-actionPrimary text-14 font-700'
                                                : 'pb-8 text-neutral-3 text-14 font-700'
                                        }
                                    >
                                        Other Settings
                                    </p>
                                </div>
                            </div>
                        </div>
                        {selectedTab === SELECTED_TAB.CONFIG && (
                            <div className="p-16 flex flex-col flex-1 space-y-16 h-full overflow-auto">
                                <div className="p-16 rounded-8 bg-white space-y-8">
                                    <p className="body-1 bold text-darwin-neutral-1000">
                                        Order of fields in “Medications Table”
                                    </p>
                                    <ElementsConfigureTable
                                        config={elements}
                                        onUpdate={this.updateElemets}
                                    />
                                    <RoutesConfig enabled={this.state.medRouteEnabled} onChange={(checked: boolean) => this.setState({
                                        medRouteEnabled: checked
                                    })} />
                                </div>
                            </div>
                        )}
                        {selectedTab === SELECTED_TAB.TYPES && (
                            <div className="p-16 flex flex-col flex-1 space-y-16 h-full overflow-auto">
                                <div className="p-16 rounded-8 bg-white space-y-8">
                                    <p className="body-1 bold text-darwin-neutral-1000">
                                        Select Medicine Types You Prescribe
                                    </p>
                                    <MedicationType
                                        medTypeIds={medTypeIds}
                                        updateMedType={this.updateMedType}
                                    />
                                </div>
                            </div>
                        )}
                        {selectedTab === SELECTED_TAB.OTHER_SETTINGS && (
                            <div className="p-16 flex flex-col flex-1 space-y-16 h-full overflow-auto">
                                <div className="p-16 rounded-8 bg-white space-y-8">
                                    <MedicationsOtherSettings
                                        isDoseCalculatorAvailable={isDoseCalculatorAvailable}
                                        toggleIsDoseCalculatorAvailable={
                                            this.toggleIsDoseCalculatorAvailable
                                        }
                                    />
                                </div>
                            </div>
                        )}
                        <div className="flex items-center justify-between px-16 py-8 space-x-8 border-t border-neutral-7">
                            <div>
                                <button
                                    onClick={closeMe}
                                    className="neutral-btn btn--small px-16 py-8"
                                >
                                    <span className="w-full text-center">Cancel</span>
                                </button>
                            </div>
                            <div>
                                <button
                                    onClick={this.onSave}
                                    className="primary-btn  px-12 py-8 btn btn--small space-x-8"
                                >
                                    <span className="w-full text-center">Save</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </RightPopUp>
            </ReactHotkeys>
        );
    }
}

export default MedicationConfigPanel;
